'use client'

import { useEffect, useSyncExternalStore } from "react"
import { usePathname } from "next/navigation"
import { formatPrice } from "@/utils/util"
import { ShoppingBag } from "lucide-react"
import { navigateUrl } from "@/utils/client-request"
import { useCart, useLoginForm } from "@/lib/commerce/hooks"
import Link from "next/link"
import Skeleton from "@/components/elements/Skeleton"
import CheckoutCard from "./CheckoutCard"
import EmptyState from "../elements/EmptyState"
import { TextField } from "../elements/Field"
import { authLink, authPrimaryButton, authSecondaryButton } from "../auth/authStyles"

const eyebrow = 'text-[11px] tracking-[0.14em] uppercase text-muted-foreground'
const subscribeNoop = () => () => {}

export default function CheckoutLogin({ vendor }: { vendor: any }) {
  const pathname = usePathname()
  const { cart, initialLoading, isValidating, validateCart, canCheckout, amount, sale, activeOrderDiscount, orderDiscountAmount, appliedCoupon, cartTotal, loyaltyDiscountAmount } = useCart()
  const loyaltyDiscount = loyaltyDiscountAmount > 0 ? Math.min(loyaltyDiscountAmount, Math.max(0, cartTotal - orderDiscountAmount)) : 0
  const f = useLoginForm({ defaultRedirect: '/checkout' })
  const hasMounted = useSyncExternalStore(subscribeNoop, () => true, () => false)

  const products = cart?.products || []

  useEffect(() => {
    if (products.length > 0 && vendor) validateCart()
  }, [products.length, vendor])

  if (!hasMounted) return null

  const orderDiscountNote = (() => {
    if (!(activeOrderDiscount?.type === 'order' && !appliedCoupon && activeOrderDiscount.minOrderAmount > 0)) return null
    const subPrice = amount - sale
    const minAmt = activeOrderDiscount.minOrderAmount
    const target = activeOrderDiscount.targets?.find((t: any) => t.role === 'target' && t.targetId === null)
    if (!target) return null
    const discountLabel = target.valueType === 'percent' ? `${target.valuePercent}%-ийн хөнгөлөлт` : target.valueType === 'amount' ? `${formatPrice(target.valueAmount)} хөнгөлөлт` : null
    if (!discountLabel) return null
    const pct = Math.max(0, Math.min((subPrice / minAmt) * 100, 100))
    return (
      <div className="pt-6">
        <div className="flex items-baseline justify-between gap-4 text-[13px]">
          <span>{formatPrice(minAmt)}-с дээш захиалгад {discountLabel}</span>
          <span className="shrink-0 text-muted-foreground tabular-nums">{subPrice >= minAmt ? `-${formatPrice(orderDiscountAmount)}` : `${formatPrice(minAmt - subPrice)} дутуу`}</span>
        </div>
        <div className="mt-2 h-0.5 bg-border"><div className="h-full bg-foreground transition-[width] duration-500" style={{ width: `${pct}%` }} /></div>
      </div>
    )
  })()

  return (
    <div className="w-full px-4 lg:px-6 pt-8 lg:pt-12 pb-20 lg:grid lg:grid-cols-2 lg:gap-20">
      <section className="lg:border-r lg:border-border lg:pr-20 lg:flex lg:items-center">
        <div className="w-full max-w-sm mx-auto lg:py-12">
          <h1 className="text-3xl sm:text-4xl tracking-tight text-foreground">Нэвтрэх</h1>
          <p className="mt-3 text-sm text-muted-foreground">Захиалгаа үргэлжлүүлэхийн тулд бүртгэлдээ нэвтэрнэ үү</p>

          <form onSubmit={f.handleLogin} className="mt-10 flex flex-col gap-5">
            <TextField label="Утасны дугаар / Имэйл" type="text" placeholder="Утасны дугаар эсвэл имэйл хаяг" value={f.emailOrPhone} onChange={e => f.setEmailOrPhone(e.target.value)} onKeyDown={f.handleKeyDown} autoComplete="username" autoFocus />
            <div>
              <TextField label="Нууц үг" type="password" placeholder="Таны нэвтрэх нууц үг" value={f.password} onChange={e => f.setPassword(e.target.value)} onKeyDown={f.handleKeyDown} autoComplete="current-password" />
              <div className="mt-2 flex justify-end">
                <Link href={navigateUrl('/forgot', pathname)} className={`text-xs ${authLink}`}>Нууц үгээ мартсан уу?</Link>
              </div>
            </div>
            <button type="submit" disabled={f.loading} className={`mt-2 ${authPrimaryButton}`}>{f.loading ? 'Нэвтэрч байна…' : 'Нэвтрэх'}</button>
          </form>

          {vendor?.canGuestOrder && (
            <div className="mt-8">
              <div className="flex items-center gap-4 text-xs text-muted-foreground">
                <span className="flex-1 h-px bg-border" />
                Эсвэл
                <span className="flex-1 h-px bg-border" />
              </div>
              <button type="button" onClick={() => { window.location.href = navigateUrl('/checkout', pathname) }} disabled={isValidating || !canCheckout()} className={`mt-8 ${authSecondaryButton}`}>
                Зочноор үргэлжлүүлэх
              </button>
              <p className="mt-4 border-l border-foreground pl-4 text-xs leading-relaxed text-muted-foreground">Зочноор захиалсан тохиолдолд таны захиалгын түүх хадгалагдахгүй.</p>
            </div>
          )}

          <p className="mt-8 pt-8 border-t border-border text-sm text-muted-foreground">
            Шинэ хэрэглэгч үү?{' '}
            <button type="button" onClick={f.routeToRegister} className={authLink}>Бүртгэл үүсгэх</button>
          </p>
        </div>
      </section>

      <section className="mt-16 lg:mt-0">
        <h2 className={eyebrow}>Таны захиалга</h2>
        {initialLoading ? (
          <div className="mt-4 divide-y divide-border">
            {Array.from({ length: 3 }).map((_, index) => (
              <div key={index} className="flex gap-4 py-5">
                <Skeleton className="w-16 aspect-3/4 rounded-none" />
                <div className="flex-1 space-y-2 pt-1">
                  <Skeleton className="h-3.5 w-2/3 rounded-none" />
                  <Skeleton className="h-3 w-1/3 rounded-none" />
                </div>
              </div>
            ))}
          </div>
        ) : products.length > 0 ? (
          <>
            <ul className="mt-4 divide-y divide-border border-y border-border">
              {products.map((product: any, index: number) => (
                <li key={index} className="py-5"><CheckoutCard product={product} /></li>
              ))}
            </ul>
            <dl className="mt-6 space-y-3 text-sm">
              <div className="flex items-baseline justify-between gap-4"><dt className="text-muted-foreground">Үнийн дүн</dt><dd className="tabular-nums">{formatPrice(cartTotal)}</dd></div>
              {!products.every((p: any) => p.isDigital === true) && (
                <div className="flex items-baseline justify-between gap-4"><dt className="text-muted-foreground">Хүргэлт</dt><dd className="text-muted-foreground">Хаягаа оруулсны дараа</dd></div>
              )}
              {orderDiscountAmount > 0 && (
                <div className="flex items-baseline justify-between gap-4"><dt className="text-muted-foreground">Захиалгын хөнгөлөлт</dt><dd className="tabular-nums">-{formatPrice(orderDiscountAmount)}</dd></div>
              )}
              {loyaltyDiscount > 0 && (
                <div className="flex items-baseline justify-between gap-4"><dt className="text-muted-foreground">Лояалти хөнгөлөлт</dt><dd className="tabular-nums">-{formatPrice(loyaltyDiscount)}</dd></div>
              )}
              <div className="flex items-baseline justify-between gap-4 pt-3 text-lg"><dt>Нийт дүн</dt><dd className="tabular-nums">{formatPrice(Math.max(0, cartTotal - orderDiscountAmount - loyaltyDiscount))}</dd></div>
            </dl>
            {orderDiscountNote}
            {cart?.products?.some((product: any) => product.quantity === 0 || product.isOutOfStock) && (
              <div className="mt-6 border-l border-foreground pl-4">
                <p className="text-[13px]">Үлдэгдэл дууссан бараа</p>
                <p className="mt-1 text-xs leading-relaxed text-muted-foreground">Таны сагсанд үлдэгдэл хүрэлцэхгүй бараа байна, шалгаад үргэлжлүүлнэ үү</p>
              </div>
            )}
          </>
        ) : (
          <EmptyState icon={ShoppingBag} title="Таны сагс хоосон байна" description="Та яг одоо сагсандаа бараа нэмээрэй!">
            <a href={navigateUrl('/product', pathname)} className="inline-flex items-center h-11 px-8 border border-foreground text-[13px] tracking-wide transition-colors duration-150 hover:bg-foreground hover:text-background focus-visible:outline-1 focus-visible:outline-offset-4 focus-visible:outline-foreground">Дэлгүүр хэсэх</a>
          </EmptyState>
        )}
      </section>
    </div>
  )
}
